import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Save, Eye, FileDown, Send, CheckCircle2, FileSignature, XCircle, Trash2, QrCode, Sparkles, Languages } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { PageHeader, Section, StatusBadge, Field, Loading, SendResultDialog, useBusy } from "../components/common";
import CustomerPicker from "../components/CustomerPicker";
import PhotoGallery from "../components/PhotoGallery";
import PendingPhotos from "../components/PendingPhotos";
import { ShareDialog } from "../components/ShareDialog";
import { AIDraftDialog, EST_TEXT } from "../components/ai/AIDraftDialog";
import { money, todayStr, pdfUrl, fmtDate, fullName, photoUrl } from "../lib/helpers";

const W = { en: (y) => `${y} year workmanship warranty`, es: (y) => `Garantía de mano de obra de ${y} año(s)` };

function Preview({ f, customer, company, photos, onClose, showPrices }) {
  const es = f.language === "es";
  const L = (en, sp) => (es ? sp : en);
  const block = (title, text) => text ? <div className="mt-4"><div className="text-[#D9531E] font-bold text-sm">{title}</div><div className="whitespace-pre-wrap text-sm">{text}</div></div> : null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="estimate-preview-dialog">
        <DialogHeader><DialogTitle>{L("Preview", "Vista previa")}</DialogTitle></DialogHeader>
        <div className="border rounded-xl p-5 bg-white">
          <div className="flex justify-between border-b-2 border-[#D9531E] pb-3">
            <div className="text-sm"><div className="font-bold">{company?.name}</div><div className="text-slate-500">{company?.phone} {company?.email}</div></div>
            <div className="text-right"><div className="text-2xl font-extrabold">{L("ESTIMATE", "PRESUPUESTO")}</div><div className="text-xs text-slate-500">#{f.number || "—"} · {fmtDate(f.date)}</div></div>
          </div>
          <div className="mt-3 text-sm"><b>{L("Customer", "Cliente")}:</b> {fullName(customer)}<br /><b>{L("Property", "Propiedad")}:</b> {f.property_address}</div>
          {block(L("Project description", "Descripción del proyecto"), f.project_description)}
          {block(L("Scope of work", "Alcance del trabajo"), f.scope_of_work)}
          {block(L("Preparation & Protection", "Preparación y protección"), f.preparation)}
          {block(L("Materials", "Materiales"), f.materials)}
          {photos?.length > 0 && (
            <div className="mt-4" data-testid="estimate-preview-photos">
              <div className="text-[#D9531E] font-bold text-sm">{L("PROJECT PHOTOS", "FOTOS DEL PROYECTO")}</div>
              <div className="grid grid-cols-2 gap-3 mt-2">{photos.map((p, i) => (
                <figure key={i}><img src={p.url} alt="" className="w-full h-40 object-contain bg-slate-50 rounded" /><figcaption className="text-xs text-slate-600 mt-1 text-center">{[p.caption, p.area].filter(Boolean).join(" · ")}</figcaption></figure>
              ))}</div>
            </div>
          )}
          {block(L("Warranty", "Garantía"), f.warranty)}
          {block(L("Exclusions", "Exclusiones"), f.exclusions)}
          {block(L("Project Duration", "Duración del proyecto"), f.duration)}
          {block(L("Notes", "Notas"), f.notes)}
          {showPrices && <div className="mt-5 ml-auto max-w-xs text-sm grid gap-1">
            <div className="flex justify-between font-bold bg-orange-50 p-2 rounded"><span>{L("Total price", "Precio total")}</span><span className="mono">{money(f.price)}</span></div>
            <div className="flex justify-between px-2"><span>{L("Deposit", "Depósito")} ({f.deposit_percent}%)</span><span className="mono">{money(f.price * f.deposit_percent / 100)}</span></div>
            <div className="flex justify-between px-2"><span>{L("Remaining balance", "Saldo restante")}</span><span className="mono">{money(f.price - f.price * f.deposit_percent / 100)}</span></div>
          </div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function EstimateEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { t } = useLang();
  const { can } = useAuth();
  const [f, setF] = useState(null);
  const [meta, setMeta] = useState({});
  const [customer, setCustomer] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState({});
  const [preview, setPreview] = useState(false);
  const [pending, setPending] = useState([]);
  const [pvPhotos, setPvPhotos] = useState([]);
  const [sendDlg, setSendDlg] = useState(false);
  const [sent, setSent] = useState(null);
  const [share, setShare] = useState(false);
  const [aiDlg, setAiDlg] = useState(false);
  const [busy, run] = useBusy();

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data));
    api.get("/settings").then((r) => {
      setSettings(r.data);
      if (isNew) {
        setF({ customer_id: params.get("customer") || "", property_address: "", date: todayStr(), expiration_date: "", language: "en", service: "",
          project_description: "", scope_of_work: "", preparation: "", duration: "", materials: "", materials_cost: 0, labor_cost: 0, price: 0,
          deposit_percent: r.data.default_deposit_percent ?? 50, warranty: W.en(r.data.default_warranty_years ?? 1), exclusions: "", notes: "" });
        setMeta({ status: "draft" });
      }
    });
    if (!isNew) api.get(`/estimates/${id}`).then((r) => { const { customer: c, followups, contract, ...e } = r.data; setF(e); setCustomer(c); setMeta({ status: e.status, number: e.number, followups, contract }); });
  }, [id, isNew, params]);

  useEffect(() => { if (!isNew && params.get("share") === "1") setShare(true); }, [isNew, params]);

  useEffect(() => {
    if (isNew && f?.customer_id && !customer) api.get(`/customers/${f.customer_id}`).then((r) => pickCustomer(r.data.customer));
  }, [f?.customer_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!f) return <Loading />;
  const canPrice = can("view_prices");
  if (isNew && !canPrice) return <><PageHeader back title={t("New Estimate", "Nuevo presupuesto")} /><div className="card p-6 text-slate-600" data-testid="estimate-no-price-permission">{t("You need the 'See prices' permission to create estimates.", "Necesita el permiso 'Ver precios' para crear presupuestos.")}</div></>;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });
  const pickCustomer = (c) => {
    setCustomer(c);
    if (c) setF((p) => ({ ...p, customer_id: c.id, property_address: p.property_address || c.property_address || "", language: c.preferred_language || "en", warranty: W[c.preferred_language || "en"](settings.default_warranty_years ?? 1), service: p.service || c.service_requested || "" }));
  };
  const applyTemplate = (tid) => {
    const tp = templates.find((x) => x.id === tid);
    if (!tp) return;
    const l = f.language === "es" ? "es" : "en";
    setF({ ...f, service: tp[`name_${l}`] || tp.name_en, scope_of_work: tp[`scope_${l}`] || f.scope_of_work, materials: tp[`materials_${l}`] || f.materials, exclusions: tp[`exclusions_${l}`] || f.exclusions, warranty: W[l](tp.warranty_years || 1) });
  };
  const locked = meta.status === "approved" || meta.status === "lost";
  const ro = locked || !canPrice;
  const uploadPending = async (eid) => {
    for (const p of pending) {
      const fd = new FormData();
      fd.append("files", p.file);
      fd.append("estimate_id", eid);
      fd.append("category", "estimate");
      fd.append("caption", p.caption);
      fd.append("area", p.area);
      fd.append("include_in_pdf", p.include ? "true" : "false");
      try { await api.post("/photos", fd); } catch (e) { toast.error(errMsg(e)); }
    }
    if (pending.length) toast.success(`${pending.length} ${t("photo(s) saved", "foto(s) guardada(s)")}`);
    setPending([]);
  };
  const openPreview = async () => {
    if (isNew) setPvPhotos(pending.filter((p) => p.include).map((p) => ({ url: p.url, caption: p.caption, area: p.area })));
    else {
      const { data } = await api.get("/photos", { params: { estimate_id: id } });
      setPvPhotos(data.filter((p) => p.include_in_pdf).map((p) => ({ url: photoUrl(p.id), caption: p.caption, area: p.area })));
    }
    setPreview(true);
  };
  const save = async (quiet) => {
    if (!f.customer_id) { toast.error(t("Select a customer", "Seleccione un cliente")); throw new Error("no customer"); }
    try {
      const { data } = isNew ? await api.post("/estimates", f) : await api.put(`/estimates/${id}`, f);
      if (!quiet) toast.success(t("Draft saved", "Borrador guardado"));
      if (isNew) { await uploadPending(data.id); nav(`/estimates/${data.id}`, { replace: true }); }
      else { setF((p) => ({ ...p, ...data })); }
      return data.id;
    } catch (e) { toast.error(errMsg(e)); throw e; }
  };
  const pdf = () => run(async () => {
    const w = window.open("", "_blank");
    try { const eid = await save(true); const url = pdfUrl(`/estimates/${eid}/pdf`); if (w) w.location.href = url; else window.location.href = url; } catch { w?.close(); }
  });
  const send = (channel) => run(async () => {
    try { const eid = ro ? id : await save(true); const { data } = await api.post(`/estimates/${eid}/send`, { channel }); setSendDlg(false); setSent(data); setMeta((m) => ({ ...m, status: m.status === "draft" ? "sent" : m.status })); } catch (e) { if (e.response) toast.error(errMsg(e)); }
  });
  const action = (path, msg) => run(async () => {
    try {
      if (!isNew && !ro) await save(true);
      const { data } = await api.post(`/estimates/${id}/${path}`);
      toast.success(msg);
      if (path === "convert") return nav(`/contracts/${data.id}`);
      setMeta((m) => ({ ...m, status: data.status }));
    } catch (e) { if (e.response) toast.error(errMsg(e)); }
  });
  const del = async () => {
    if (!window.confirm(t("Delete this estimate?", "¿Eliminar este presupuesto?"))) return;
    await api.delete(`/estimates/${id}`); nav("/estimates");
  };
  const deposit = (f.price * f.deposit_percent) / 100;
  const aiOk = !ro && can("ai_assistant");
  const translate = () => run(async () => {
    const target = f.language === "es" ? "en" : "es";
    try {
      const { data } = await api.post("/ai/translate", { target, fields: Object.fromEntries(EST_TEXT.map((k) => [k, f[k] || ""])) });
      setF((p) => ({ ...p, ...data.fields, language: target }));
      toast.success(t("Translated – review and save", "Traducido – revise y guarde"));
    } catch (e) { toast.error(errMsg(e)); }
  });
  const approvePrice = () => run(async () => {
    try { await api.post(`/ai/estimates/${id}/approve-price`); setF((p) => ({ ...p, ai_price_pending: false })); toast.success(t("Price approved", "Precio aprobado")); } catch (e) { toast.error(errMsg(e)); }
  });
  const inp = (k, l, type = "text") => <Field label={l}><input className="inp" type={type} step="any" value={f[k] ?? ""} onChange={set(k)} disabled={ro} data-testid={`estimate-${k}-input`} /></Field>;
  const txt = (k, l) => <Field label={l} ai={ro ? null : { form: "estimate", field: k, value: f[k], onInsert: (v) => setF((p) => ({ ...p, [k]: v })), ctx: { customer_id: f.customer_id, estimate_id: isNew ? "" : id } }}><textarea className="inp" value={f[k] || ""} onChange={set(k)} disabled={ro} data-testid={`estimate-${k}-input`} /></Field>;
  return (
    <>
      <PageHeader back title={isNew ? t("New Estimate", "Nuevo presupuesto") : `${t("Estimate", "Presupuesto")} #${meta.number}`}
        subtitle={!isNew && <StatusBadge status={meta.status} testid="estimate-status-badge" />} />
      <div className="grid lg:grid-cols-[1fr_340px] gap-4 items-start">
        <div className="grid gap-4">
          <Section title={t("Customer & property", "Cliente y propiedad")} testid="estimate-customer-section">
            <div className="grid gap-3 p-4">
              {isNew ? <CustomerPicker value={f.customer_id} onChange={(cid, c) => pickCustomer(c)} testid="estimate-customer" /> : <Link to={`/customers/${f.customer_id}`} className="font-semibold text-[#D9531E]">{fullName(customer)}</Link>}
              {customer && <div className="text-sm text-slate-500" data-testid="estimate-customer-contact">{customer.phone || "—"} · {customer.email || "—"}</div>}
              {inp("property_address", t("Property address", "Dirección de la propiedad"))}
              <div className="grid grid-cols-3 gap-3">
                {inp("date", t("Date", "Fecha"), "date")}
                {inp("expiration_date", t("Expires", "Vence"), "date")}
                <Field label={t("Document language", "Idioma del documento")}>
                  <select className="inp" value={f.language} onChange={set("language")} disabled={ro} data-testid="estimate-language-select"><option value="en">English</option><option value="es">Español</option></select>
                </Field>
              </div>
            </div>
          </Section>
          <Section title={t("Work", "Trabajo")} testid="estimate-work-section">
            <div className="grid gap-3 p-4">
              {aiOk && (
                <div className="flex flex-wrap gap-2" data-testid="estimate-ai-tools">
                  <button type="button" className="btn-outline btn-sm" onClick={() => setAiDlg(true)} data-testid="estimate-ai-write-btn"><Sparkles className="w-4 h-4 text-[#D9531E]" /> {t("Write with AI", "Escribir con IA")}</button>
                  <button type="button" className="btn-outline btn-sm" disabled={busy} onClick={translate} data-testid="estimate-ai-translate-btn"><Languages className="w-4 h-4" /> {f.language === "es" ? t("Translate to English", "Traducir a inglés") : t("Translate to Spanish", "Traducir a español")}</button>
                </div>
              )}
              {!ro && (
                <Field label={t("Use a template", "Usar una plantilla")}>
                  <select className="inp" value="" onChange={(e) => applyTemplate(e.target.value)} data-testid="estimate-template-select">
                    <option value="">{t("Choose service template…", "Elija una plantilla…")}</option>
                    {templates.map((tp) => <option key={tp.id} value={tp.id}>{f.language === "es" ? tp.name_es || tp.name_en : tp.name_en}</option>)}
                  </select>
                </Field>
              )}
              {inp("service", t("Project title", "Título del proyecto"))}
              {txt("project_description", t("Project description", "Descripción del proyecto"))}
              {txt("scope_of_work", t("Detailed scope of work (use new lines or • for bullets)", "Alcance detallado (use líneas nuevas o • para viñetas)"))}
              {txt("preparation", t("Preparation & protection", "Preparación y protección"))}
              {txt("materials", t("Materials", "Materiales"))}
              {txt("exclusions", t("Exclusions", "Exclusiones"))}
              {txt("warranty", t("Workmanship warranty", "Garantía de mano de obra"))}
              {inp("duration", t("Estimated project duration", "Duración estimada del proyecto"))}
              {txt("notes", t("Additional notes", "Notas adicionales"))}
            </div>
          </Section>
          {isNew ? <PendingPhotos items={pending} setItems={setPending} />
            : <PhotoGallery filter={{ estimate_id: id }} categories={["estimate"]} uploadFields={{ estimate_id: id }} includeToggle title={t("PROJECT PHOTOS", "FOTOS DEL PROYECTO")} addLabel={t("+ Add Photos", "+ Agregar fotos")} />}
        </div>
        <div className="grid gap-4 lg:sticky lg:top-20">
          {canPrice && <Section title={t("Price", "Precio")} testid="estimate-price-section">
            <div className="grid gap-3 p-4">
              <div className="grid grid-cols-2 gap-3">{inp("materials_cost", t("Materials $", "Materiales $"), "number")}{inp("labor_cost", t("Labor $", "Mano de obra $"), "number")}</div>
              {!locked && (f.materials_cost > 0 || f.labor_cost > 0) && <button className="btn-ghost btn-sm" onClick={() => setF({ ...f, price: Number(f.materials_cost) + Number(f.labor_cost) })} data-testid="estimate-use-subtotal-btn">{t("Use materials + labor as price", "Usar materiales + mano de obra como precio")} ({money(Number(f.materials_cost) + Number(f.labor_cost))})</button>}
              {inp("price", t("Total price $", "Precio total $"), "number")}
              {f.ai_price_pending && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm grid gap-2" data-testid="estimate-ai-price-pending">
                  <span>{t("Price suggested by AI. Review it and approve before sending.", "Precio sugerido por la IA. Revíselo y apruébelo antes de enviar.")}</span>
                  <button className="btn-primary btn-sm" disabled={busy} onClick={approvePrice} data-testid="estimate-ai-price-approve-btn">{t("Approve price", "Aprobar precio")}</button>
                </div>
              )}
              {inp("deposit_percent", t("Deposit %", "Depósito %"), "number")}
              <div className="rounded-xl bg-slate-50 p-3 grid gap-1 text-sm">
                <div className="flex justify-between"><span>{t("Deposit", "Depósito")}</span><span className="mono font-semibold" data-testid="estimate-deposit-amount">{money(deposit)}</span></div>
                <div className="flex justify-between"><span>{t("Remaining balance", "Saldo restante")}</span><span className="mono font-semibold" data-testid="estimate-remaining-balance">{money(f.price - deposit)}</span></div>
              </div>
              <p className="text-xs text-slate-500">{t("Tracking only. No payments are processed.", "Solo seguimiento. No se procesan pagos.")}</p>
            </div>
          </Section>}
          <div className="grid grid-cols-2 gap-2" data-testid="estimate-actions">
            {!ro && <button className="btn-outline" disabled={busy} onClick={() => run(() => save().catch(() => {}))} data-testid="estimate-save-draft-btn"><Save className="w-5 h-5" /> {t("Save Draft", "Guardar")}</button>}
            <button className="btn-outline" onClick={openPreview} data-testid="estimate-preview-btn"><Eye className="w-5 h-5" /> {t("Preview", "Vista previa")}</button>
            {canPrice && <button className="btn-outline" disabled={busy} onClick={pdf} data-testid="estimate-pdf-btn"><FileDown className="w-5 h-5" /> {t("Generate PDF", "Generar PDF")}</button>}
            <button className="btn-primary" disabled={busy} onClick={() => setSendDlg(true)} data-testid="estimate-send-btn"><Send className="w-5 h-5" /> {t("Send", "Enviar")}</button>
            {!isNew && <button className="btn-outline col-span-2" onClick={() => setShare(true)} data-testid="estimate-share-btn"><QrCode className="w-5 h-5" /> {t("Share: link, QR & PDF", "Compartir: enlace, QR y PDF")}</button>}
            {!isNew && meta.status !== "approved" && <button className="btn-outline text-emerald-700" disabled={busy} onClick={() => action("approve", t("Approved", "Aprobado"))} data-testid="estimate-approve-btn"><CheckCircle2 className="w-5 h-5" /> {t("Mark Approved", "Marcar aprobado")}</button>}
            {!isNew && <button className="btn-dark" disabled={busy} onClick={() => action("convert", t("Contract created", "Contrato creado"))} data-testid="estimate-convert-btn"><FileSignature className="w-5 h-5" /> {meta.contract ? t("Open Contract", "Ver contrato") : t("Convert to Contract", "Convertir a contrato")}</button>}
            {!isNew && meta.status !== "lost" && meta.status !== "approved" && <button className="btn-danger" disabled={busy} onClick={() => action("lost", t("Marked lost", "Marcado perdido"))} data-testid="estimate-lost-btn"><XCircle className="w-5 h-5" /> {t("Mark Lost", "Marcar perdido")}</button>}
            {!isNew && can("delete_records") && <button className="btn-danger" onClick={del} data-testid="estimate-delete-btn"><Trash2 className="w-5 h-5" /></button>}
          </div>
          {meta.followups?.length > 0 && (
            <Section title={t("Follow-up schedule", "Calendario de seguimiento")} testid="estimate-followups">
              {meta.followups.map((x) => <div key={x.id} className="row-link !min-h-[48px] text-sm"><span className="flex-1">{x.step} {t("days", "días")} · {fmtDate(x.due_date)}</span><StatusBadge status={x.status} /></div>)}
            </Section>
          )}
        </div>
      </div>
      {preview && <Preview f={{ ...f, number: meta.number }} customer={customer} company={settings.company} photos={pvPhotos} showPrices={canPrice} onClose={() => setPreview(false)} />}
      {sendDlg && (
        <Dialog open onOpenChange={(o) => !o && setSendDlg(false)}>
          <DialogContent className="max-w-sm" data-testid="estimate-send-dialog">
            <DialogHeader><DialogTitle>{t("Send estimate", "Enviar presupuesto")}</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-500">{t("The customer gets a secure link to view the estimate and PDF. Follow-ups are scheduled automatically.", "El cliente recibe un enlace seguro para ver el presupuesto y PDF. Los seguimientos se programan automáticamente.")}</p>
            <button className="btn-primary" disabled={busy} onClick={() => send("sms")} data-testid="estimate-send-sms-btn">{t("By text message", "Por mensaje de texto")}</button>
            <button className="btn-outline" disabled={busy} onClick={() => send("email")} data-testid="estimate-send-email-btn">{t("By email", "Por correo")}</button>
          </DialogContent>
        </Dialog>
      )}
      <SendResultDialog data={sent} onClose={() => setSent(null)} />
      {aiDlg && <AIDraftDialog f={f} onClose={() => setAiDlg(false)} onApply={(fields) => setF((p) => ({ ...p, ...Object.fromEntries(Object.entries(fields).filter(([, v]) => v)) }))} onPrice={(amount) => { setF((p) => ({ ...p, price: amount })); toast.success(t("Price applied – save to keep it", "Precio aplicado – guarde para conservarlo")); }} />}
      {share && <ShareDialog kind="estimate" id={id} number={meta.number} onClose={() => setShare(false)} onShared={(a) => a !== "pdf" && setMeta((m) => ({ ...m, status: m.status === "draft" ? "sent" : m.status }))} />}
    </>
  );
}
